import { Show, useRecordContext } from 'react-admin';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { EmptyHint, FieldRow, Section } from '../../components/Section';
import { StatusChip } from '../../components/StatusChip';
import { ReportLink, UserLink } from '../../components/EntityLink';
import { ResolveReportButtons } from './ReportActions';
import {
  formatDateTime,
  label,
  reportCategoryLabels,
  reportStatusLabels,
  userStatusLabels,
} from '../../i18n/labels';
import type { ReportDetail } from '../../types';

function ReportTitle(): JSX.Element {
  const record = useRecordContext<ReportDetail>();
  return <span>{record ? `Жалоба: ${label(reportCategoryLabels, record.category)}` : ''}</span>;
}

function ReportShowLayout(): JSX.Element | null {
  const record = useRecordContext<ReportDetail>();
  if (!record) return null;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Section title="Жалоба" action={<ResolveReportButtons />}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          {label(reportCategoryLabels, record.category)}
          <StatusChip status={record.status} dict={reportStatusLabels} />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <UserLink id={record.reporter.id} label={record.reporter.displayName} />
          <ArrowForwardIcon fontSize="small" color="disabled" />
          <UserLink id={record.targetUser.id} label={record.targetUser.displayName} />
          <StatusChip status={record.targetUser.status} dict={userStatusLabels} />
          <Typography variant="body2" color="text.secondary">
            рейтинг {record.targetUser.rating ?? '—'}
          </Typography>
        </Box>
        <FieldRow label="Комментарий заявителя" value={record.comment} />
        <FieldRow label="Подана" value={formatDateTime(record.createdAt)} />
        {record.reviewedAt ? (
          <>
            <FieldRow label="Рассмотрена" value={formatDateTime(record.reviewedAt)} />
            <FieldRow label="Заметки модератора" value={record.moderatorNotes} />
          </>
        ) : null}
      </Section>

      <Section title={`Скриншоты (${record.screenshotUrls.length})`}>
        {record.screenshotUrls.length === 0 ? (
          <EmptyHint text="Скриншоты не приложены" />
        ) : (
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {record.screenshotUrls.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt="Скриншот жалобы"
                  style={{
                    maxHeight: 320,
                    maxWidth: 240,
                    borderRadius: 8,
                    display: 'block',
                  }}
                />
              </a>
            ))}
          </Stack>
        )}
      </Section>

      <Section title={`История жалоб на этого пользователя (${record.previousReports.length})`}>
        {record.previousReports.length === 0 ? (
          <EmptyHint text="Других жалоб на пользователя нет" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Категория</TableCell>
                <TableCell>От кого</TableCell>
                <TableCell>Решение</TableCell>
                <TableCell>Заметки модератора</TableCell>
                <TableCell>Когда</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {record.previousReports.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{label(reportCategoryLabels, r.category)}</TableCell>
                  <TableCell>
                    <UserLink id={r.reporter.id} label={r.reporter.displayName} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.status} dict={reportStatusLabels} />
                  </TableCell>
                  <TableCell>{r.moderatorNotes ?? '—'}</TableCell>
                  <TableCell>{formatDateTime(r.createdAt)}</TableCell>
                  <TableCell>
                    <ReportLink id={r.id} label="Открыть" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

export function ReportShow(): JSX.Element {
  return (
    <Show title={<ReportTitle />} component="div">
      <ReportShowLayout />
    </Show>
  );
}
